import { ApolloClient, ApolloLink, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import * as Sentry from '@sentry/react';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import { toast } from 'components/Toast/use-toast';
import Cookies from 'js-cookie';

import { expiredTokenError } from './refreshTokenError';

const httpLink = createUploadLink({
    uri: process.env.REACT_APP_API_URL,
    credentials: 'include', // Include cookies in requests
});

const authLink = setContext(async (_, { headers }) => {
    // get the authentication token from cookies
    const token = Cookies.get('token');

    // return the headers to the context so httpLink can read them
    return {
        headers: {
            ...headers,
            authorization: token ? `JWT ${token}` : '',
        },
    };
});

// Add Sentry error logging
const sentryErrorLink = onError(({ graphQLErrors, networkError, operation, response }) => {
    // Log GraphQL errors with 400-like status
    if (graphQLErrors) {
        graphQLErrors.forEach((error) => {
            const extensions = error.extensions as any;

            // Log all GraphQL errors to Sentry
            Sentry.captureException(error, {
                tags: {
                    type: 'graphql_error',
                    operation: operation.operationName,
                },
                extra: {
                    message: error.message,
                    path: error.path,
                    extensions: extensions,
                    variables: operation.variables,
                    query: operation.query.loc?.source.body,
                    response: response,
                },
            });
        });
    }

    // Log network errors (including 400 status codes)
    if (networkError) {
        const statusCode = (networkError as any)?.statusCode;
        const result = (networkError as any)?.result;
        const bodyText = (networkError as any)?.bodyText;

        // Log 400 errors specifically
        if (statusCode === 400) {
            Sentry.captureException(networkError, {
                tags: {
                    type: 'network_error_400',
                    status_code: 400,
                    operation: operation.operationName,
                },
                extra: {
                    operation: operation.operationName,
                    variables: operation.variables,
                    query: operation.query.loc?.source.body,
                    message: networkError.message,
                    statusCode: statusCode,
                    response: response ? JSON.stringify(response, null, 2) : null,
                    result: result,
                    bodyText: bodyText,
                    networkError: JSON.stringify(networkError, Object.getOwnPropertyNames(networkError)),
                },
            });
        } else if (statusCode && statusCode >= 400) {
            // Log other 4xx and 5xx errors as well
            Sentry.captureException(networkError, {
                tags: {
                    type: 'network_error',
                    status_code: statusCode,
                    operation: operation.operationName,
                },
                extra: {
                    operation: operation.operationName,
                    variables: operation.variables,
                    query: operation.query.loc?.source.body,
                    message: networkError.message,
                    response: response ? JSON.stringify(response, null, 2) : null,
                    statusCode: statusCode,
                    result: result,
                    bodyText: bodyText,
                    networkError: JSON.stringify(networkError, Object.getOwnPropertyNames(networkError)),
                },
            });
        }

        // Show rate limit message for 429
        if (statusCode === 429) {
            const errorMessage = result?.errors?.[0]?.message || result?.detail || result?.message;
            toast({
                title: 'Rate Limited',
                description: errorMessage || 'Too many requests. Please wait before trying again.',
                variant: 'warning',
            });
        }

        // Show a toast for server errors (5xx) so the user knows something went wrong
        if (statusCode && statusCode >= 500) {
            toast({
                title: 'Server Error',
                description: 'An unexpected server error occurred. Please try again later.',
                variant: 'warning',
            });
        }
    
    }
});

export const client = new ApolloClient({
    link: ApolloLink.from([sentryErrorLink, expiredTokenError, authLink, httpLink]),
    cache: new InMemoryCache(),
    defaultOptions: {
        mutate: {
            errorPolicy: 'all',
        },
        query: {
            fetchPolicy: 'no-cache',
            errorPolicy: 'all',
        },
    },
});
